/* eslint-disable camelcase */
/* eslint-disable react-perf/jsx-no-new-object-as-prop */
import React, { useCallback, useMemo, useState } from 'react'
import {
    Anchor,
    Box,
    Text,
} from '@usap/uicomponents'
import { outsideClickHandler } from '@usap/core'
import commaSeparetedValue from '../../../helpers/commaSeparetedValue'
import SetKitDisplayWMO from '../SetKitDisplayWMO'
import creditStatusMapping from '../RMA/Mapping/creditStatusMapping'
import returnRequestStatusMapping from '../RMA/Mapping/returnRequestStatusMapping'
import ViewReturnDetailsButtonWMO from './button/ViewReturnDetailsButtonWMO'
import { getButtonType } from '../OrderHistory/components/buttons/manageButtonType'
import useGetLifetimeReplacementFlag from '../RMA/hooks/useGetLifetimeReplacementFlag'
import { CorePriceBadge, CoreInfoUpdated } from '../../product'
import ProductImageWMO from './Product/ImageWMO'
import ProductInformationWMO from './Product/InformationWMO'

import {
    Container,
    ResponsiveContainer,
    OrderItemBox,
    OrderDetailsBox,
    OrderDetailsTextBox,
    ActionContainer,
    CorePriceContainer,
    ProductImageContainer,
    QuantityContainer,
    Divider,
    DesktopKitContainer,
    MobileKitContainer,
    OrderStatusContainer,
    ProductProtectionMainSection,
    ProductProtectionDescription,
} from './OrderItemWMO.css'
import ViewReturnInProgressDetailsButtonWMO from '../ViewReturnInProgressDetailsButtonWMO'
import getTitleAndMessage from './hooks/getTitleAndMessage'
import BuyItAgainButtonWMO from './button/BuyItAgainButtonWMO'
import ProductProtectionPlan from '../../Checkout/betaWidgets/ProductProtectionPlan'
import { OrderDetailsCtaWmo } from './button/style/buttonWMO.css'
import { getMostRecentShippingDate, GTM, isItemEligibleForReturn } from '@usap/carparts/src/shared/helpers'
import { useMembershipStorage } from '@usap/carparts/src/shared/hooks/useMembershipStorage'
import { isMembershipSku } from '../../../helpers'

const ToggleCore = ({ product, coreConfig }) => {
    const [showInformation, shouldShowInformation] = useState(false)
    const hasCorePrice = Boolean(product.itemTotalCore) && Number(product.itemTotalCore) !== 0
    const corePrice = commaSeparetedValue(product.itemTotalCore)
    const corePriceEach = product.qty > 1 ? ` ($${commaSeparetedValue(product.corePrice)} each)` : ''

    const handleShowInformation = useCallback(
        () => {
            outsideClickHandler(
                showInformation,
                [`iconClick${product.oid}`],
                () => {
                    shouldShowInformation(false)
                },
            )
            shouldShowInformation(!showInformation)
        },
        [product.oid, shouldShowInformation, showInformation],
    )

    return hasCorePrice && (
        <CorePriceContainer>
            {showInformation && (
                <CoreInfoUpdated forAccount config={coreConfig} />
            )}

            <Text size="xsmall" color="dark-2" truncate style={{ fontSize:'12px !important'}}>
                {`Core Charge: $${corePrice}${corePriceEach}`}
            </Text>
            <CorePriceBadge forAccount uniqueId={product.oid} onClick={handleShowInformation} showCorePrice={showInformation} />
        </CorePriceContainer>
    )
}

export default function OrderItem({
    product,
    config,
    isDesktop,
    orderDetails,
    itemDetails,
    setReturnDetailsData,
    setDisplayReturnDetails,
    navigateToRMA,
    creditStatus,
    returnRequestStatus,
    orderId,
    formatBrand,
    OpenChatHandler,
    coreConfig,
    productProtectionPlan,
    isEppSubtext,
    unfoldedShippingConfig,
}) {
    const vehicle = useMemo(() => (product && product.vehicle) || {}, [product])

    const core = useMemo(
        () => (<ToggleCore product={product} coreConfig={coreConfig} />),
        [product, coreConfig],
    )
    const itemStatus = product.itemStatus || 'Pending'
    const { displayEddOrderDetailsPage = false } = config || {}

    const {
        title, messaging, showReturn, showTrack,
    } = getTitleAndMessage(product, itemStatus, displayEddOrderDetailsPage)
    const label = useMemo(() => <Text size="small">Return Item</Text>, [])

    const returnStatusData = useMemo(() => {
        const { merchantOrderId, emailAddress, orderId } = orderDetails
        let messageData = ''

        if (itemDetails.returnRequest && itemDetails.returnRequest.length === 0) return null

        const {
            // eslint-disable-next-line camelcase
            credit_status_code,
            order_item_id,
            credit_id,
            credit_status,
            rma_id,
            return_request_id,
            return_request_status,
        } = itemDetails.returnRequest || {}

        if (credit_status) {
            messageData = creditStatusMapping(credit_status_code, creditStatus)
        }

        // Priority: Display RMA ID
        if (typeof itemDetails.returnRequest !== 'undefined' &&
            itemDetails.returnRequest.rma_id) {
            return {
                message: messageData.main_reason || null,
                idType: 'RMA ID:',
                rmaId: rma_id,
                returnRequestId: return_request_id || null,
                orderItemId: order_item_id,
                orderId: orderDetails.orderId,
                merchantOrderId,
                email: emailAddress,
                creditId: credit_id,
            }
        }

        if (credit_status) {
            messageData = creditStatusMapping(credit_status_code, creditStatus)

            return {
                message: messageData.main_reason || null,
                idType: 'Credit ID:',
                rmaId: rma_id,
                orderItemId: order_item_id,
                orderId,
                merchantOrderId,
                email: emailAddress,
                creditId: credit_id,
            }
        }

        // Display Return Request ID
        if (typeof itemDetails.returnRequest !== 'undefined' && itemDetails.returnRequest.return_request_id !== '') {
            messageData = returnRequestStatusMapping(return_request_status, returnRequestStatus)

            return {
                message: messageData.main_reason || null,
                idType: 'Return Request ID:',
                returnRequestId: return_request_id,
                rmaId: rma_id || null,
                merchantOrderId,
                email: emailAddress,
                orderId: orderDetails.orderId,
                orderItemId: order_item_id,
            }
        }

        return null
    }, [product])

    const isLifetimeReplacement = useGetLifetimeReplacementFlag()

    const buttonData = useMemo(() => ({
        itemDetails,
        showReturn,
        navigateToRMA,
        partNumber: product?.partNumber,
        orderId,
        type: isLifetimeReplacement ? getButtonType() : '',
    }), [
        itemDetails,
        product,
        orderId,
        navigateToRMA,
        showReturn,
        isLifetimeReplacement,
    ])

    const OpenChatBox = useCallback(e => {
        e.preventDefault()
        OpenChatHandler()
    }, [])

    const hasEpp = Boolean(itemDetails.productProtection)
    const selectedEpp = itemDetails?.productProtection
    const eppSubtext = productProtectionPlan?.subtext ?? ''
    const eppSubtextMsg = selectedEpp?.category === 'Wheel Tire' || selectedEpp?.category === 'Wheel' ? eppSubtext?.wheels : eppSubtext?.auto
    const eppAmount = parseFloat(selectedEpp?.price) || 0.00
    const eppYear = selectedEpp?.type
    const eppStartLink = 'https://customers.extend.com/en-US/'
    const eachAmount = itemDetails?.qty > 1 ? `($${parseFloat(selectedEpp?.price / itemDetails?.qty).toFixed(2)} each)` : ''

    const startEppClickHandler = () => {
        const productProtectionTotal = eppAmount
        const orderTotal = product?.itemTotalAmount
        const eppPartName = product?.partName
        const eppSku = product?.partNumber
        GTM.productProtectionOrderDetailsStartClaim({ productProtectionTotal, orderTotal, eppPartName, eppSku })

        window.open(eppStartLink, '_blank');
    }

    const isItemShipped = title == 'Shipped' || title == 'Order Shipped' || title == 'Delivered';
    const { eligible: itemEligibleForReturn } = isItemEligibleForReturn(itemDetails?.parentSku ? getMostRecentShippingDate(itemDetails?.parentSku?.shipping) : itemDetails?.trackingCreation);
    const { membershipStorage: { membership_status } } = useMembershipStorage();
    const isMembershipItem = isMembershipSku(product?.partNumber);
    const showViewMembershipCTA = isMembershipItem && membership_status === 'ACTIVE';

    const gtmBuyAgainClick = () => {
        const itemDetailsObj = {
            price: itemDetails?.regularPrice || 0,
            part_number: itemDetails?.partNumber || '',
            part_name: itemDetails?.title || '',
        }
        GTM.buyAgainClick(itemDetailsObj);
    }

    return (
        <>
            <Container data-testid="order-details-item-container">
                <ResponsiveContainer wrap>
                    <OrderItemBox>
                        <OrderStatusContainer>
                            {
                                isMembershipItem ?
                                    <Text size='16px' weight={600} color='#282832'>{'Enrolled'}</Text>
                                    :
                                    <>
                                        <Text size='16px' weight={600} color='#282832'>{!returnStatusData ? title : returnStatusData.message}</Text>
                                        {returnStatusData ? <Text size='14px' weight={400} color='#282832'>Return Request ID: <Text size='14px' weight={600} color='#282832'>{returnStatusData.returnRequestId}</Text></Text> : null}
                                    </>
                            }
                        </OrderStatusContainer>
                        <OrderDetailsBox>
                            <ProductImageContainer>
                                <ProductImageWMO product={product} />
                                {!isMembershipItem && (
                                    <QuantityContainer>
                                        <Text size="14px" weight={500}>
                                            QTY: {product.qty}
                                        </Text>
                                    </QuantityContainer>
                                )}
                            </ProductImageContainer>
                            <Box style={{ flex: 1 }}>
                                <OrderDetailsTextBox>
                                    <ProductInformationWMO
                                      vehicle={vehicle}
                                      product={product}
                                      core={core}
                                      formatBrand={formatBrand}
                                      isDesktop={isDesktop}
                                      isMembershipItem={isMembershipItem}
                                      unfoldedShippingConfig={unfoldedShippingConfig}
                                    />
                                </OrderDetailsTextBox>
                                <DesktopKitContainer>
                                    <SetKitDisplayWMO
                                        parentSku={product.parentSku}
                                        style={{ margin: '8pt 4px', borderRadius: '4pt' }}
                                    />
                                </DesktopKitContainer>
                            </Box>

                        </OrderDetailsBox>
                        <MobileKitContainer>
                            <SetKitDisplayWMO
                                parentSku={product.parentSku}
                                style={{ margin: '8pt 4px', borderRadius: '4pt' }}
                            />
                        </MobileKitContainer>
                    </OrderItemBox>
                    {
                        isDesktop && !isMembershipItem ? <Divider /> :
                            showViewMembershipCTA ? <Divider /> : null
                    }
                    {
                        !isMembershipItem ?
                            (
                                <ActionContainer
                                    gap="small"
                                >
                                    <BuyItAgainButtonWMO productUri={product?.productUri} itemStatus={itemStatus} isDesktop={isDesktop} gtmBuyAgainClick={gtmBuyAgainClick} />
                                    {returnStatusData && (
                                        <ViewReturnInProgressDetailsButtonWMO
                                            returnStatusData={returnStatusData}
                                            orderDetails={orderDetails}
                                            creditStatus={creditStatus}
                                            returnRequestStatus={returnRequestStatus}
                                            setReturnDetailsData={setReturnDetailsData}
                                            setDisplayReturnDetails={setDisplayReturnDetails}
                                            isDesktop={isDesktop}
                                        />
                                    )}
                                    {isItemShipped && !returnStatusData && itemEligibleForReturn && (
                                        <ViewReturnDetailsButtonWMO
                                            data={buttonData}
                                            isDesktop={isDesktop}
                                        />
                                    )}
                                </ActionContainer>
                            )
                            :
                            showViewMembershipCTA ?
                                <ActionContainer>
                                    <OrderDetailsCtaWmo
                                        custom
                                        hideBorder
                                        onClick={() =>
                                            window.location.href = '/my-membership'
                                        }
                                        label={<Text size={isDesktop ? '16px' : '14px'} color={'dark-2'}>View Membership</Text>}
                                    />
                                </ActionContainer>
                                :
                                null
                    }
                </ResponsiveContainer>
                {hasEpp && <Divider style={{ flexDirection: 'row', margin: '24px 0' }} />}
                {
                    hasEpp ?
                        <ProductProtectionMainSection>
                            <ProductProtectionDescription>
                                <ProductProtectionPlan
                                    year={eppYear}
                                    amount={eppAmount}
                                    eppSubtextMsg={eppSubtextMsg}
                                    isEppSubtext={isEppSubtext}
                                    desktopMode
                                    eachAmount={eachAmount}
                                    selectedEpp={selectedEpp}
                                />
                                <ProductProtectionPlan
                                    year={eppYear}
                                    amount={eppAmount}
                                    eppSubtextMsg={eppSubtextMsg}
                                    isEppSubtext={isEppSubtext}
                                    desktopMode={false}
                                    eachAmount={eachAmount}
                                    selectedEpp={selectedEpp}
                                />
                            </ProductProtectionDescription>
                            {isDesktop && <Divider />}
                            {
                                title == 'Delivered'
                                &&
                                <Box style={{ flex: 1 }}>
                                    <OrderDetailsCtaWmo
                                        custom
                                        hideBorder
                                        label={<Text size={isDesktop ? '16px' : '14px'} color={'dark-2'} weight={600}>Start Product Protection Claim</Text>}
                                        onClick={startEppClickHandler}
                                    />
                                </Box>
                            }
                        </ProductProtectionMainSection>
                        :
                        null
                }
            </Container>
        </>
    )
}
